import axiosInstance from './api'
import CookieService from './cookie'
import TokenService from './token'

let refreshTokenError = false
// The refresh token that was in the cookie when the failing refresh started. Lets a tab recover
// once another tab/app restores the shared session (login or rotation) with a different token.
let failedRefreshToken = null
let isAlreadyFetchingAccessToken = false
let subscribers = []

function onAccessTokenFetched(error = null) {
  isAlreadyFetchingAccessToken = false

  // execute callbacks:
  subscribers = subscribers.filter((callback) => callback(error))
}

function addSubscriber(callback) {
  subscribers.push(callback)
}

function formatError(error) {
  const errors = {
    _error:
      error.response.data['hydra:description'] ||
      error.response.data['detail'] ||
      error.response.statusText,
    status: error.response.status,
    statusText: error.response.statusText,
    message:
      error.response.data['message'] ||
      error.response.data['detail'] ||
      error.response.data['hydra:title'],
    errorType: error.response.data['errorType'],
    code: error.response.data['code']
  }

  if (error.response.data.violations) {
    error.response.data.violations.map((violation) =>
      Object.assign(errors, {[violation.propertyPath]: violation.message})
    )
    Object.assign(errors, {violations: error.response.data.violations})
  }

  return errors
}

function getCurrentRefreshToken() {
  try {
    return TokenService.getLocalRefreshToken()
  } catch {
    return null
  }
}

function isCredentialEndpoint(url) {
  return url.includes('/login_check') || url.includes('/token/refresh') || url.includes('/email_confirmation')
}

const setup = (refreshTokenCallback, options = { ignoreSwitchUser: false }) => {
  axiosInstance.interceptors.request.use(
    (config) => {
      const token = TokenService.getLocalToken()
      const locale = localStorage.getItem('locale')

      if (token) {
        config.headers['Authorization'] = 'Bearer ' + token
      }

      if (locale) {
        config.headers['X-LOCALE'] = locale
      }

      const switchUser = CookieService.getSwitchUser()
      const ignoreSwitchUser = config.ignoreSwitchUser

      if (switchUser && !ignoreSwitchUser && !options.ignoreSwitchUser) {
        config.headers['X-Switch-User'] = switchUser
      }

      // cache control:
      config.headers['Cache-Control'] = 'must-revalidate, no-store, no-cache, private'

      return config
    },
    (error) => {
      return Promise.reject(error)
    }
  )

  axiosInstance.interceptors.response.use(
    async (response) => {
      if (isCredentialEndpoint(response.config.url)) {
        refreshTokenError = false
        failedRefreshToken = null
      }

      return response
    },
    async (error) => {
      const originalConfig = error.config

      if (error.request.responseType === 'blob') {
        error.response.data = JSON.parse(await error.response.data.text())
      }

      if (!error.response) {
        return Promise.reject(error)
      }

      if (refreshTokenError) {
        const currentRefreshToken = getCurrentRefreshToken()

        // The session cookie is shared, so a login or refresh in another tab/app can restore it
        // after our refresh failed — only stay in the error state while it still holds the token
        // that failed.
        if (!currentRefreshToken || currentRefreshToken === failedRefreshToken) {
          return Promise.reject(formatError(error))
        }

        refreshTokenError = false
        failedRefreshToken = null
      }

      if (error.response.status === 401 && !isCredentialEndpoint(originalConfig.url)) {
        if (!isAlreadyFetchingAccessToken) {
          isAlreadyFetchingAccessToken = true

          const sentRefreshToken = getCurrentRefreshToken()

          // Resolves both on a successful refresh and when another tab/app already rotated the
          // token (AuthService.refresh() leaves the newer cookie in place) — either way the
          // subscribers retry below, and the request interceptor picks up the cookie's current token.
          refreshTokenCallback()
            .then(() => {
              onAccessTokenFetched()
            })
            .catch((error) => {
              isAlreadyFetchingAccessToken = false
              refreshTokenError = true
              failedRefreshToken = sentRefreshToken

              onAccessTokenFetched(error)
            })
        }

        return new Promise((resolve, reject) => {
          addSubscriber((error) => {
            // refresh token error:
            if (error) {
              return reject(error)
            }

            return resolve(axiosInstance(originalConfig))
          })
        })
      }

      return Promise.reject(formatError(error))
    }
  )
}

export default setup
